import React from 'react';
import { Link } from 'react-router-dom';
import { useCookieConsent } from '@context/CookieConsentContext';
import { legalConfig as cfg } from '../legal/legalConfig';

/**
 * Public footer. Only links to pages/actions that actually exist — items from
 * the legal pack that need real content (Safety centre, Help centre, PAIA
 * Manual, social accounts) are intentionally omitted until they are live.
 */
const Footer: React.FC = () => {
  const { openSettings } = useCookieConsent();
  const year = new Date().getFullYear();

  return (
    <footer className="mt-20 border-t border-gray-200 pt-10 pb-8 text-sm text-gray-500">
      <div className="grid gap-8 md:grid-cols-4">
        <div className="md:col-span-1">
          <div className="flex items-center gap-2 mb-3">
            <img src="/logo.png" alt="RoomZa" className="w-8 h-8 rounded-lg" />
            <span className="font-bold text-gray-900">{cfg.brandName}</span>
          </div>
          <p className="text-gray-500 max-w-xs">
            Find compatible roommates across South Africa, connect safely, and make space feel like home.
          </p>
          <Link
            to="/"
            className="inline-block mt-3 text-pink-600 hover:text-pink-700 transition-colors"
          >
            roomzza.com
          </Link>
        </div>

        <div>
          <h3 className="font-semibold text-gray-900 mb-3">Discover</h3>
          <ul className="space-y-2">
            <li>
              <Link to="/how-it-works" className="hover:text-pink-600 transition-colors">
                How {cfg.brandName} works
              </Link>
            </li>
            <li>
              <Link to="/signup?role=roommate_seeker" className="hover:text-pink-600 transition-colors">
                Find a roommate
              </Link>
            </li>
            <li>
              <Link to="/signup?role=landlord" className="hover:text-pink-600 transition-colors">
                For landlords
              </Link>
            </li>
          </ul>
        </div>

        <div>
          <h3 className="font-semibold text-gray-900 mb-3">Account</h3>
          <ul className="space-y-2">
            <li>
              <Link to="/tenant/profile" className="hover:text-pink-600 transition-colors">
                Manage or delete account
              </Link>
            </li>
          </ul>
        </div>

        <div>
          <h3 className="font-semibold text-gray-900 mb-3">Legal</h3>
          <ul className="space-y-2">
            <li>
              <Link to="/legal/terms" className="hover:text-pink-600 transition-colors">
                Terms of Use
              </Link>
            </li>
            <li>
              <Link to="/legal/privacy" className="hover:text-pink-600 transition-colors">
                Privacy Policy
              </Link>
            </li>
            <li>
              <Link to="/legal/community-guidelines" className="hover:text-pink-600 transition-colors">
                Community Guidelines
              </Link>
            </li>
            <li>
              <Link to="/legal/paia" className="hover:text-pink-600 transition-colors">
                PAIA Manual
              </Link>
            </li>
            <li>
              <Link to="/legal/cookies" className="hover:text-pink-600 transition-colors">
                Cookie Notice
              </Link>
            </li>
            <li>
              <button
                type="button"
                onClick={openSettings}
                className="hover:text-pink-600 transition-colors"
              >
                Cookie settings
              </button>
            </li>
          </ul>
        </div>
      </div>

      <div className="mt-10 pt-6 border-t border-gray-100 text-xs text-gray-400 leading-relaxed">
        <p>
          &copy; {year} {cfg.entityName}. All rights reserved. {cfg.brandName} helps people discover
          potential roommates and communicate with invited landlords. {cfg.brandName} is not a
          landlord, tenant, estate agent, credit provider, or party to any lease unless we expressly
          state otherwise.
        </p>
      </div>
    </footer>
  );
};

export default Footer;
